import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  Typography,
  Divider,
  Box,
  useTheme,
  IconButton,
  Stack,
  Button,
  Tooltip,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import * as CONFIG from '@/config'
import {
  Agent,
  Dashboard,
  Form,
  Knowledge,
  Maximize,
  Question,
  Quiz,
  Star,
  Users,
} from '../UI/Icons'
import { CustomLink } from './CustomLink'
import logoHead from '@/assets/images/logo-head.svg'
import Logo from '@/assets/New-images/logo.svg'
import { useGlobalContext } from '@/context/GlobalContext'

interface SidebarItem {
  icon: ReactNode
  label: string
  link: string
}

const windowObj: any = window

interface SidebarProps {
  isCollapsed: boolean
  toggleCollapse: () => void
  isMobile?: boolean
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const AgentSidebarLinks: FC<SidebarProps> = ({
  isCollapsed,
  toggleCollapse,
  isMobile = false,
}) => {
  const { t } = useTranslation('translation')
  const history = useHistory()
  const location = useLocation()
  const theme = useTheme()
  const { viewMode } = useGlobalContext()

  const [currentLogo, setCurrentLogo] = useState(isCollapsed ? logoHead : Logo)

  useEffect(() => {
    if (isCollapsed) {
      const timer = setTimeout(() => {
        setCurrentLogo(logoHead)
      }, theme.transitions.duration.enteringScreen)
      return () => clearTimeout(timer)
    } else {
      setCurrentLogo(Logo)
    }
  }, [isCollapsed, theme.transitions.duration.enteringScreen])

  useEffect(() => {
    const selectedAgent = localStorage.getItem('selectedAgent')
    const searchParams = new URLSearchParams(location.search)

    if (selectedAgent && !searchParams.has('agent')) {
      searchParams.append('agent', selectedAgent)
      history.replace({ search: searchParams.toString() })
    }
  }, [location, history])

  // Check if Forms should be shown (default: true, unless explicitly set to false)
  const isFormEnabled = windowObj.FORM_ENABLED !== false

  const categories: {
    category: string
    items: SidebarItem[]
  }[] = [
    {
      category: '#',
      items: [
        {
          icon: <Dashboard />,
          label: t('Dashboard'),
          link: CONFIG.OVERVIEW,
        },
      ],
    },
    {
      category: t('Set Up'),
      items: [
        {
          icon: <Knowledge />,
          label: t('Knowledge'),
          link: CONFIG.KNOWLEDGE_ALL,
        },
        {
          icon: <Quiz />,
          label: t('Activities'),
          link: CONFIG.EXERCISES,
        },
        ...(isFormEnabled
          ? [
              {
                icon: <Form />,
                label: t('Forms'),
                link: '/forms',
              },
            ]
          : []),
      ],
    },
    {
      category: t('Configure'),
      items: [
        {
          icon: <Agent />,
          label: t('Agent'),
          link: `${CONFIG.CHAT_CUSTOMISATION}`,
        },
        {
          icon: <Users />,
          label: t('Users'),
          link: `${CONFIG.USER_MANAGEMENT}`,
        },
      ],
    },
  ]

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : isCollapsed ? 70 : 200,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // overflowX: 'hidden', // Moved to inner box
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <IconButton
        onClick={toggleCollapse}
        sx={{
          position: 'absolute',
          display: isMobile ? 'none' : 'flex',
          right: -15,
          top: 20,
          backgroundColor: 'white',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #eaeaea',
          width: 30,
          height: 30,
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          zIndex: 9999,
        }}
      >
        {isCollapsed ? (
          <ChevronRightIcon fontSize="small" />
        ) : (
          <ChevronLeftIcon fontSize="small" />
        )}
      </IconButton>

      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden', // Move overflow hidden here
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            pl: 2.5,
            py: 2,
            height: 64, // fixed height for consistency
            minWidth: { xs: 'auto', md: 200 }, // Responsive: no min-width on mobile, 200px on desktop
          }}
        >
          <img
            src={currentLogo}
            alt="Logo"
            style={{
              height: currentLogo === logoHead ? '28px' : '31px',
              width: 'auto',
              maxWidth: 'none', // Prevent shrinking
              objectFit: 'contain',
              objectPosition: 'left', // Ensure left side (icon) stays visible
            }}
          />
        </Box>

        <Stack flexGrow={1} sx={{ overflow: 'auto', gap: 2 }}>
          {categories.map(({ category, items }, categoryIndex) =>
            category === '#' ? (
              <List key={categoryIndex} disablePadding>
                {items.map((item, index) => (
                  <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
                ))}
              </List>
            ) : (
              <Box key={categoryIndex}>
                {!isCollapsed && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      px: 2,
                      color: '#79767A',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {category}
                  </Typography>
                )}
                {isCollapsed && <Divider sx={{ my: 1, mx: 2 }} />}
                <List disablePadding sx={{ mt: 0.5 }}>
                  {items.map((item, index) => (
                    <CustomLink
                      key={index}
                      {...item}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </List>
              </Box>
            ),
          )}
        </Stack>

        {viewMode !== 'embedded' && (
          <Tooltip
            title={isCollapsed ? t('Learner View') : ''}
            placement="right"
          >
            <Button
              variant="contained"
              href={`${windowObj.CHATBOT_URL}?agent=${localStorage.getItem(
                'selectedAgent',
              )}&through=admin-portal`}
              target="_blank"
              sx={{
                mx: 1.5,
                minHeight: 40,
                minWidth: isCollapsed ? 40 : 'auto',
                justifyContent: 'center',
                px: isCollapsed ? 1 : 2.5,
                width: isCollapsed ? 40 : 'calc(100% - 24px)',
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Mulish, sans-serif',
                fontSize: '0.875rem',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: 'none',
                  color: 'white',
                },
              }}
            >
              {isCollapsed ? (
                <Maximize />
              ) : (
                <>
                  <Box
                    component="span"
                    sx={{
                      minWidth: 24,
                      display: 'flex',
                      justifyContent: 'center',
                      mr: 2.5,
                    }}
                  >
                    <Maximize />
                  </Box>
                  {t('Learner View')}
                </>
              )}
            </Button>
          </Tooltip>
        )}

        <Divider sx={{ pt: 2 }} />

        <Box sx={{ py: 2 }}>
          <CustomLink
            icon={<Star />}
            label={t("What's New")}
            link="https://noodlefactory-support.notion.site/New-Releases-247a6979da7e80a3a505e9189a3b1e6a"
            isExternal
            isCollapsed={isCollapsed}
          />
          <CustomLink
            icon={<Question />}
            label={t('Support')}
            link={CONFIG.SUPPORT}
            isCollapsed={isCollapsed}
          />
        </Box>
      </Box>
    </Box>
  )
}

interface CustomSidebarProps {
  isCollapsed: boolean
  toggleCollapse: () => void
  isMobile?: boolean
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const AgentSidebar: FC<CustomSidebarProps> = ({
  isCollapsed,
  toggleCollapse,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
}) => {
  const theme = useTheme()

  return (
    <>
      {/* Mobile Drawer - temporary */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: '75vw', sm: '280px' },
              maxWidth: '320px',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              border: 'none',
            },
          }}
        >
          <AgentSidebarLinks
            isCollapsed={false}
            toggleCollapse={onMobileClose || (() => {})}
            isMobile={true}
          />
        </Drawer>
      )}

      {/* Desktop Drawer - persistent */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isCollapsed ? 70 : 200,
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              border: 'none',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflow: 'visible',
            },
          }}
          open
        >
          <AgentSidebarLinks
            isCollapsed={isCollapsed}
            toggleCollapse={() => {
              toggleCollapse()
            }}
            isMobile={false}
          />
        </Drawer>
      )}
    </>
  )
}

export default AgentSidebar
